import React, { useEffect, ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAbp } from 'core/hooks/useAbp';
import { setAccessToken } from 'core/auth/accessTokenStorage';
import { AppInitLoader } from '../ui/AppInitLoader';

interface AbpProviderProps {
  children: ReactNode;
  fallback?: ReactNode;
}

const ErrorFallback = ({ error, onRetry }: { error: string; onRetry: () => void }) => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center p-8 bg-red-50 rounded-lg max-w-md mx-4">
      <div className="text-red-600 mb-4">
        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-red-800 mb-2">Erreur d'initialisation ABP</h3>
      <p className="text-red-600 text-sm mb-4">{error}</p>
      <div className="space-x-2">
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
        >
          Recharger
        </button>
      </div>
    </div>
  </div>
);

export const AbpProvider: React.FC<AbpProviderProps> = ({
  children,
  fallback = <AppInitLoader />
}) => {
  const auth = useAuth();
  const { initialized, loading, error, initialize } = useAbp();

  // Sync OIDC token to this tab's storage synchronously before any child renders.
  // This ensures interceptors (axios, fetch) have the token available
  // when AbpProvider, RoleProvider, or any child makes API calls.
  if (auth.isAuthenticated && auth.user?.access_token) {
    setAccessToken(auth.user.access_token);
  }

  useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    if (auth.isAuthenticated && !initialized && !loading) {
      initialize();
    }
  }, [auth.isAuthenticated, auth.isLoading, initialized, loading, initialize]);

  // Si OIDC est en cours de chargement
  if (auth.isLoading) {
    return <>{fallback}</>;
  }

  // Si ABP est en cours de chargement
  if (loading) {
    return <>{fallback}</>;
  }

  // Si erreur ABP et utilisateur authentifié
  if (error && auth.isAuthenticated && !initialized) {
    return <ErrorFallback error={error} onRetry={() => {
      initialize();
    }} />;
  }

  // Si utilisateur non authentifié, on laisse passer (les guards s'en occupent)
  if (!auth.isAuthenticated) {
    return <>{children}</>;
  }

  // Si utilisateur authentifié mais ABP pas initialisé
  if (auth.isAuthenticated && !initialized) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
